import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
  Animated,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { CommonActions } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, Club } from '../types';
import { clubsService, userService, authService } from '../services';
import { useDialog } from '../contexts';
import { ScreenLayout, ClubLogo } from '../components';
import { Colors, Radii, Fonts, Shadows, StatusTints, Typography } from '../constants';

type Props = NativeStackScreenProps<RootStackParamList, 'onboarding'>;

export const OnboardingScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();

  // Page state (0 = welcome, 1 = club selection, 2 = completion)
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [allClubs, setAllClubs] = useState<Club[]>([]);
  const [filteredClubs, setFilteredClubs] = useState<Club[]>([]);
  const [selectedClubIds, setSelectedClubIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingClubs, setLoadingClubs] = useState(true);
  const dialog = useDialog();

  // Animation values
  const slideAnim = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(1)).current;

  const MAX_CLUBS = 10;

  useEffect(() => {
    loadClubs();
  }, []);

  useEffect(() => {
    // Filter clubs based on search query
    if (searchQuery.trim() === '') {
      setFilteredClubs(allClubs);
    } else {
      const filtered = allClubs.filter(club =>
        club.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredClubs(filtered);
    }
  }, [searchQuery, allClubs]);

  const loadClubs = async () => {
    try {
      setLoadingClubs(true);
      const clubs = await clubsService.getClubs();
      setAllClubs(clubs);
      setFilteredClubs(clubs);
    } catch (error) {
      dialog.alert(
        t('common.errors.error'),
        t('screens.onboarding.errors.loadClubsFailed'),
        'error'
      );
    } finally {
      setLoadingClubs(false);
    }
  };

  const handleToggleClub = (clubId: string) => {
    setSelectedClubIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clubId)) {
        newSet.delete(clubId);
      } else {
        if (newSet.size >= MAX_CLUBS) {
          dialog.alert(
            t('screens.onboarding.errors.maxClubsReached'),
            t('screens.onboarding.errors.maxClubsMessage', { max: MAX_CLUBS }),
            'warning'
          );
          return prev;
        }
        newSet.add(clubId);
      }
      return newSet;
    });
  };

  const animateToPage = (targetPage: number) => {
    // Fade out
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: targetPage > currentPage ? -50 : 50,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      // Update page
      setCurrentPage(targetPage);

      // Reset and fade in
      slideAnim.setValue(targetPage > currentPage ? 50 : -50);
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    });
  };

  const handleNext = () => {
    if (currentPage < 2) {
      animateToPage(currentPage + 1);
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      animateToPage(currentPage - 1);
    }
  };

  const handleSkipClubSelection = () => {
    animateToPage(2);
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      // Complete onboarding with selected clubs
      await userService.completeOnboarding(
        selectedClubIds.size > 0 ? Array.from(selectedClubIds) : []
      );

      // Refresh cached user data
      await authService.getCurrentUser(true);

      // Force app refresh to trigger AppNavigator re-render
      // The AppNavigator uses conditional rendering based on user.onboarding_completed
      // We need to reload the app for it to re-check the user state
      if (Platform.OS === 'web') {
        // On web, navigate to root which will trigger re-authentication check
        window.location.href = '/';
      } else {
        // On mobile, use navigation reset to restart the navigation stack
        // This will cause AppNavigator to re-render and check onboarding status
        navigation.dispatch(
          CommonActions.reset({
            index: 0,
            routes: [{ name: 'Home' as any }],
          })
        );
      }
    } catch (error) {
      dialog.alert(
        t('common.errors.error'),
        t('screens.onboarding.errors.completeFailed'),
        'error'
      );
      setLoading(false);
    }
  };

  if (loadingClubs) {
    return (
      <ScreenLayout showNavbar={false}>
        <View style={[styles.outerContainer, styles.centered]}>
          <ActivityIndicator size="large" color={Colors.primary} />
          <Text style={styles.loadingText}>{t('screens.onboarding.loading')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout showNavbar={false}>
      <View style={styles.outerContainer}>
        <View style={styles.card}>
          <Animated.View
            style={[
              styles.pageContainer,
              {
                opacity: fadeAnim,
                transform: [{ translateX: slideAnim }],
              },
            ]}
          >
            {currentPage === 0 && <WelcomePage onNext={handleNext} t={t} />}
            {currentPage === 1 && (
              <ClubSelectionPage
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                filteredClubs={filteredClubs}
                selectedClubIds={selectedClubIds}
                handleToggleClub={handleToggleClub}
                maxClubs={MAX_CLUBS}
                onNext={handleNext}
                onSkip={handleSkipClubSelection}
                onBack={handleBack}
                t={t}
              />
            )}
            {currentPage === 2 && (
              <CompletionPage
                selectedClubsCount={selectedClubIds.size}
                onFinish={handleFinish}
                onBack={handleBack}
                loading={loading}
                t={t}
              />
            )}
          </Animated.View>

          {/* Page indicators */}
          <View style={styles.pageIndicators}>
            {[0, 1, 2].map(page => (
              <View
                key={page}
                style={[
                  styles.pageIndicator,
                  currentPage === page && styles.pageIndicatorActive,
                ]}
              />
            ))}
          </View>
        </View>
      </View>
    </ScreenLayout>
  );
};

// ============================================================================
// PAGE 1: WELCOME
// ============================================================================

interface WelcomePageProps {
  onNext: () => void;
  t: any;
}

const WelcomePage: React.FC<WelcomePageProps> = ({ onNext, t }) => {
  return (
    <View style={styles.welcomeContainer}>
      <View style={styles.welcomeContent}>
        {/* Logo */}
        <View style={styles.logoContainer}>
          <Image
            source={require('../../Logos/clubswap_icon.png')}
            style={styles.logoImage}
            contentFit="contain"
          />
          {/* <Text style={styles.logoText}>Clubswap</Text> */}
        </View>

        <Text style={styles.welcomeTitle}>{t('screens.onboarding.welcome.title')}</Text>
        <Text style={styles.welcomeSubtitle}>
          {t('screens.onboarding.welcome.subtitle')}
        </Text>

        <View style={styles.featureList}>
          <FeatureItem
            icon="swap-horizontal"
            title={t('screens.onboarding.welcome.feature1Title')}
            description={t('screens.onboarding.welcome.feature1Description')}
          />
          <FeatureItem
            icon="people"
            title={t('screens.onboarding.welcome.feature2Title')}
            description={t('screens.onboarding.welcome.feature2Description')}
          />
          <FeatureItem
            icon="wallet"
            title={t('screens.onboarding.welcome.feature3Title')}
            description={t('screens.onboarding.welcome.feature3Description')}
          />
        </View>
      </View>

      <TouchableOpacity
        style={styles.primaryButton}
        onPress={onNext}
        activeOpacity={0.7}
      >
        <Text style={styles.primaryButtonText}>{t('screens.onboarding.welcome.getStarted')}</Text>
      </TouchableOpacity>
    </View>
  );
};

interface FeatureItemProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ icon, title, description }) => {
  return (
    <View style={styles.featureItem}>
      <View style={styles.featureIconContainer}>
        <Ionicons name={icon} size={22} color={Colors.accentStrong} />
      </View>
      <View style={styles.featureContent}>
        <Text style={styles.featureTitle}>{title}</Text>
        <Text style={styles.featureDescription}>{description}</Text>
      </View>
    </View>
  );
};

// ============================================================================
// PAGE 2: CLUB SELECTION
// ============================================================================

interface ClubSelectionPageProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredClubs: Club[];
  selectedClubIds: Set<string>;
  handleToggleClub: (clubId: string) => void;
  maxClubs: number;
  onNext: () => void;
  onSkip: () => void;
  onBack: () => void;
  t: any;
}

const ClubSelectionPage: React.FC<ClubSelectionPageProps> = ({
  searchQuery,
  setSearchQuery,
  filteredClubs,
  selectedClubIds,
  handleToggleClub,
  maxClubs,
  onNext,
  onSkip,
  onBack,
  t,
}) => {
  return (
    <View style={styles.clubSelectionContainer}>
      <View style={styles.clubSelectionContent}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{t('screens.onboarding.clubSelection.title')}</Text>
          <Text style={styles.subtitle}>
            {t('screens.onboarding.clubSelection.subtitle')}
          </Text>
        </View>

        {/* Search Bar */}
        <TextInput
          style={styles.searchInput}
          placeholder={t('screens.onboarding.clubSelection.searchPlaceholder')}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />

        {/* Selected Counter */}
        <Text style={styles.counter}>
          {t('screens.onboarding.clubSelection.counter', {
            count: selectedClubIds.size,
            max: maxClubs,
          })}
        </Text>

        {/* Clubs List */}
        <FlatList
          data={filteredClubs}
          keyExtractor={item => item.id}
          renderItem={({ item }) => (
            <ClubCard
              club={item}
              selected={selectedClubIds.has(item.id)}
              onToggle={() => handleToggleClub(item.id)}
              t={t}
            />
          )}
          style={styles.list}
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <Text style={styles.emptyText}>{t('screens.onboarding.clubSelection.noClubsFound')}</Text>
            </View>
          }
        />
      </View>

      {/* Actions */}
      <View style={styles.actions}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={onBack}
          activeOpacity={0.7}
        >
          <Text style={styles.backButtonText}>{t('screens.onboarding.clubSelection.backButton')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.skipButton}
          onPress={onSkip}
          activeOpacity={0.7}
        >
          <Text style={styles.skipButtonText}>{t('screens.onboarding.clubSelection.skipButton')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.continueButton,
            selectedClubIds.size === 0 && styles.continueButtonDisabled,
          ]}
          onPress={onNext}
          disabled={selectedClubIds.size === 0}
          activeOpacity={0.7}
        >
          <Text style={styles.continueButtonText}>{t('screens.onboarding.clubSelection.continueButton')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

// ============================================================================
// PAGE 3: COMPLETION
// ============================================================================

interface CompletionPageProps {
  selectedClubsCount: number;
  onFinish: () => void;
  onBack: () => void;
  loading: boolean;
  t: any;
}

const CompletionPage: React.FC<CompletionPageProps> = ({
  selectedClubsCount,
  onFinish,
  onBack,
  loading,
  t,
}) => {
  return (
    <View style={styles.completionContainer}>
      <View style={styles.completionContent}>
        <View style={styles.completionIconContainer}>
          <Ionicons name="checkmark-circle" size={56} color={Colors.success} />
        </View>
        <Text style={styles.completionTitle}>{t('screens.onboarding.completion.title')}</Text>
        <Text style={styles.completionSubtitle}>
          {selectedClubsCount > 0
            ? t('screens.onboarding.completion.subtitleWithClubs', { count: selectedClubsCount })
            : t('screens.onboarding.completion.subtitleNoClubs')}
        </Text>

        <View style={styles.completionInfo}>
          <Text style={styles.completionInfoTitle}>{t('screens.onboarding.completion.whatsNext')}</Text>
          <View style={styles.completionSteps}>
            <CompletionStep number="1" text={t('screens.onboarding.completion.step1')} />
            <CompletionStep number="2" text={t('screens.onboarding.completion.step2')} />
            <CompletionStep number="3" text={t('screens.onboarding.completion.step3')} />
          </View>
        </View>
      </View>

      <View style={styles.completionActions}>
        {!loading && (
          <TouchableOpacity
            style={styles.backButton}
            onPress={onBack}
            activeOpacity={0.7}
          >
            <Text style={styles.backButtonText}>{t('screens.onboarding.completion.backButton')}</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={[styles.primaryButton, styles.finishButton, loading && styles.buttonDisabled]}
          onPress={onFinish}
          disabled={loading}
          activeOpacity={0.7}
        >
          <Text style={styles.primaryButtonText}>
            {loading ? t('screens.onboarding.completion.finishing') : t('screens.onboarding.completion.finishButton')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

interface CompletionStepProps {
  number: string;
  text: string;
}

const CompletionStep: React.FC<CompletionStepProps> = ({ number, text }) => {
  return (
    <View style={styles.completionStep}>
      <View style={styles.completionStepNumber}>
        <Text style={styles.completionStepNumberText}>{number}</Text>
      </View>
      <Text style={styles.completionStepText}>{text}</Text>
    </View>
  );
};

// ============================================================================
// CLUB CARD COMPONENT
// ============================================================================

interface ClubCardProps {
  club: Club;
  selected: boolean;
  onToggle: () => void;
  t: any;
}

const ClubCard: React.FC<ClubCardProps> = ({ club, selected, onToggle, t }) => {
  return (
    <TouchableOpacity
      style={[styles.clubCard, selected && styles.clubCardSelected]}
      onPress={onToggle}
    >
      {club.logo_url && (
        <ClubLogo logoUrl={club.logo_url} size={48} style={styles.clubLogoMargin} />
      )}
      <View style={styles.clubInfo}>
        <Text style={styles.clubName}>{club.name}</Text>
        {club.description && (
          <Text style={styles.clubDescription} numberOfLines={2}>
            {club.description}
          </Text>
        )}
        {club.is_verified && (
          <Text style={styles.verifiedBadge}>{t('screens.onboarding.clubSelection.verifiedBadge')}</Text>
        )}
      </View>
      <View style={[styles.checkbox, selected && styles.checkboxSelected]}>
        {selected && <Ionicons name="checkmark" size={16} color={Colors.white} />}
      </View>
    </TouchableOpacity>
  );
};

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    padding: 24,
    backgroundColor: Colors.background,
  },
  card: {
    flex: 1,
    backgroundColor: Colors.white,
    borderRadius: Radii.hero,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    padding: 24,
    overflow: 'hidden', // Clip animated content during page transitions
    ...Shadows.cardHover,
    ...(Platform.OS === 'web' && {
      maxWidth: 900, // 3/4 of standard 1200px maxContentWidth
      alignSelf: 'center',
      width: '100%',
    }),
  },
  pageContainer: {
    flex: 1,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: Colors.textMedium,
  },

  // Page indicators
  pageIndicators: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    marginTop: 20,
  },
  pageIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.border,
  },
  pageIndicatorActive: {
    backgroundColor: Colors.primary,
    width: 24,
  },

  // Welcome Page
  welcomeContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 32,
  },
  welcomeContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  logoContainer: {
    marginBottom: 8,
    alignItems: 'center',
  },
  logoImage: {
    width: 120,
    height: 120,
    marginBottom: 16,
  },
  logoText: {
    fontFamily: Fonts.display,
    fontSize: 48,
    fontWeight: '400',
    color: Colors.textDark,
    letterSpacing: -1,
  },
  welcomeTitle: {
    fontSize: 32,
    fontFamily: Fonts.display,
    fontWeight: '400',
    color: Colors.textDark,
    marginBottom: 6,
    textAlign: 'center',
  },
  welcomeSubtitle: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    lineHeight: 22,
    color: Colors.textMedium,
    marginBottom: 32,
    textAlign: 'center',
    maxWidth: 380,
  },
  featureList: {
    width: '100%',
    marginBottom: 40,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  featureIconContainer: {
    width: 44,
    height: 44,
    borderRadius: Radii.card,
    backgroundColor: Colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
    flexShrink: 0,
  },
  featureContent: {
    flex: 1,
  },
  featureTitle: {
    fontFamily: Fonts.semiBold,
    fontSize: 16,
    lineHeight: 21,
    fontWeight: '600',
    color: Colors.textDark,
    marginBottom: 2,
  },
  featureDescription: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.textMedium,
    lineHeight: 21,
  },

  // Club Selection Page
  clubSelectionContainer: {
    flex: 1,
    justifyContent: 'space-between',
    paddingVertical: 32,
  },
  clubSelectionContent: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 26,
    fontFamily: Fonts.display,
    fontWeight: '400',
    color: Colors.textDark,
    marginBottom: 4,
  },
  subtitle: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    lineHeight: 22,
    color: Colors.textMedium,
  },
  searchInput: {
    height: 48,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.control,
    paddingHorizontal: 14,
    marginBottom: 12,
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
    backgroundColor: Colors.white,
  },
  counter: {
    ...Typography.overline,
    color: Colors.textLight,
    marginBottom: 10,
  },
  list: {
    flex: 1,
  },
  emptyState: {
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: Colors.textLight,
  },
  clubCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.control,
    marginBottom: 8,
    backgroundColor: Colors.white,
  },
  clubCardSelected: {
    borderColor: Colors.primary,
    backgroundColor: Colors.primaryLight,
  },
  clubLogoMargin: {
    marginRight: 12,
  },
  clubInfo: {
    flex: 1,
  },
  clubName: {
    fontFamily: Fonts.semiBold,
    fontSize: 15,
    lineHeight: 20,
    fontWeight: '600',
    color: Colors.textDark,
    marginBottom: 2,
  },
  clubDescription: {
    fontFamily: Fonts.regular,
    fontSize: 13,
    lineHeight: 18,
    color: Colors.textMedium,
  },
  verifiedBadge: {
    fontSize: 12,
    color: Colors.success,
    marginTop: 4,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: Colors.border,
    borderRadius: 7,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  checkboxSelected: {
    backgroundColor: Colors.primary,
    borderColor: Colors.primary,
  },

  // Completion Page
  completionContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 32,
  },
  completionContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  completionIconContainer: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: StatusTints.success.bg,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  completionTitle: {
    fontSize: 32,
    fontFamily: Fonts.display,
    fontWeight: '400',
    color: Colors.textDark,
    marginBottom: 6,
    textAlign: 'center',
  },
  completionSubtitle: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    lineHeight: 22,
    color: Colors.textMedium,
    marginBottom: 28,
    textAlign: 'center',
    maxWidth: 380,
  },
  completionInfo: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: Colors.background,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.card,
    padding: 20,
    marginBottom: 28,
  },
  completionInfoTitle: {
    ...Typography.overline,
    color: Colors.textLight,
    marginBottom: 14,
  },
  completionSteps: {
    gap: 12,
  },
  completionStep: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  completionStepNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: Colors.textDark,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    flexShrink: 0,
  },
  completionStepNumberText: {
    fontFamily: Fonts.bold,
    color: Colors.white,
    fontSize: 13,
    fontWeight: '700',
  },
  completionStepText: {
    flex: 1,
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.textDark,
  },

  // Buttons
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  completionActions: {
    width: '100%',
    flexDirection: 'row',
    gap: 12,
  },
  primaryButton: {
    backgroundColor: Colors.primary,
    padding: 16,
    borderRadius: Radii.control,
    alignItems: 'center',
    width: '100%',
  },
  primaryButtonText: {
    fontFamily: Fonts.semiBold,
    color: Colors.white,
    fontSize: 15,
    fontWeight: '600',
  },
  backButton: {
    padding: 16,
    borderRadius: Radii.control,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    backgroundColor: Colors.background,
    alignItems: 'center',
    minWidth: 80,
  },
  backButtonText: {
    fontFamily: Fonts.semiBold,
    color: Colors.textMedium,
    fontSize: 15,
    fontWeight: '600',
  },
  skipButton: {
    flex: 1,
    padding: 16,
    borderRadius: Radii.control,
    borderWidth: 1,
    borderColor: Colors.border,
    alignItems: 'center',
  },
  skipButtonText: {
    fontFamily: Fonts.semiBold,
    color: Colors.textDark,
    fontSize: 15,
    fontWeight: '600',
  },
  continueButton: {
    flex: 1,
    padding: 16,
    borderRadius: Radii.control,
    backgroundColor: Colors.primary,
    alignItems: 'center',
  },
  continueButtonText: {
    fontFamily: Fonts.semiBold,
    color: Colors.white,
    fontSize: 15,
    fontWeight: '600',
  },
  continueButtonDisabled: {
    opacity: 0.5,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  finishButton: {
    flex: 1,
  },
});
